import React, { memo, ReactNode, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { Spin } from 'antd';

import styled from 'styled-components';
import fontService, { FontWeight, TextType } from 'services/FontService';
import { mobile } from 'services/ui';
import { Colors, NetworkErrors } from 'context';
import { appState, isCareerViewer, useMyCareerCenter } from 'core';
import { authState } from 'core/state';
import { PATHS } from 'router/config';

import { CareerPreviewNotice } from 'components/pages/CareerCenter';

interface IGrowthSectionLayout {
  title: string;
  lead: string;
  children: ReactNode;
}

/**
 * Общая рамка подстраниц «Моего роста» (ТЗ 0039, доводка UX).
 *
 * Гард по роли и плашка предпросмотра нужны на КАЖДОЙ подстранице: адрес
 * теперь настоящий, и по нему можно прийти напрямую, минуя хаб. Разложить
 * одну и ту же проверку по четырём файлам значило бы гарантированно забыть её
 * в пятом.
 */
export const GrowthSectionLayout = memo(({ title, lead, children }: IGrowthSectionLayout) => {
  const { t } = useTranslation();
  const { active_role, isDemoUser } = useReactiveVar(authState);
  const { isPreview, loading } = useMyCareerCenter();

  const currentState = appState();
  useEffect(() => {
    if (!isCareerViewer(active_role) || isDemoUser) {
      appState({ ...currentState, networkError: NetworkErrors.pageIsNotAvailable });
    }
  }, [active_role, isDemoUser, currentState]);

  if (loading) {
    return (
      <Center>
        <Spin size="large" />
      </Center>
    );
  }

  return (
    <PageWrapper>
      {isPreview ? <CareerPreviewNotice /> : null}
      <Breadcrumb to={PATHS.growth}>{t('growth.backToHub')}</Breadcrumb>
      <header>
        <Title>{title}</Title>
        <Lead>{lead}</Lead>
      </header>
      {children}
    </PageWrapper>
  );
});

const PageWrapper = styled.div`
  width: 100%;
  padding: 40px 0 80px;
  display: flex;
  flex-direction: column;
  gap: 24px;

  ${mobile} {
    padding: 20px 0 48px;
  }
`;

const Breadcrumb = styled(Link)`
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}

  &:hover {
    color: ${Colors.black};
  }
`;

const Title = styled.h1`
  margin: 0;
  ${fontService.getFont({ size: 32, lineHeight: 40, weight: FontWeight.bold })}
`;

const Lead = styled.p`
  margin: 8px 0 0;
  max-width: 720px;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small })}
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
  padding: 80px 0;
`;
